/** SCR-16 — CRM Admin: register and manage partners (individual and company). */
import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { PageHeader, Card, Badge, Modal, Field, SearchInput, Drawer, Spinner } from '../../components/common/index.jsx';
import { ButtonSpinner } from '../../components/common/Loading.jsx';
import { DataTable } from '../../components/common/DataTable.jsx';
import { useAuth } from '../../hooks/useAuth.js';
import { useDebounce } from '../../hooks/useDebounce.js';
import { pushToast } from '../ui/uiSlice.js';
import { date } from '../../utils/formatters.js';
import * as api from '../../api/partnerPortal.api.js';
import { UserStatusBadge, ROLE_NAME, errorText, fieldErrors } from './partnerShared.jsx';

const BLANK = { first_name: '', last_name: '', email: '', mobile: '', business_name: '', address: '', country: '', company_name: '', registration_number: '', company_email: '', contact_person: '', contact_number: '', website: '', admin_email: '', sales_manager_id: '' };

export default function PartnerManagementPage() {
  const { can } = useAuth();
  const qc = useQueryClient();
  const dispatch = useDispatch();
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const dq = useDebounce(q, 300);
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const list = useInfiniteQuery({
    queryKey: ['partner-admin', { kind, status, dq }], queryFn: ({ pageParam }) => api.listPartners({ kind, status, q: dq, cursor: pageParam, limit: 25 }),
    initialPageParam: undefined, getNextPageParam: (l) => l.page?.next_cursor ?? undefined,
  });
  const rows = (list.data?.pages ?? []).flatMap((p) => p.data);
  const refresh = () => qc.invalidateQueries({ queryKey: ['partner-admin'] });
  const setSt = useMutation({
    mutationFn: ({ id, s }) => api.setPartnerStatus(id, s),
    onSuccess: () => { refresh(); dispatch(pushToast({ tone: 'success', message: 'Partner status updated' })); },
    onError: (e) => dispatch(pushToast({ tone: 'danger', message: 'Could not update', detail: errorText(e) })),
  });

  const columns = [
    { key: 'partner_code', header: 'Partner ID', render: (r) => <span className="fw-600">{r.partner_code}</span> },
    { key: 'name', header: 'Partner', render: (r) => (<div><div className="fw-600">{r.name}</div><div className="t-xs text-muted">{r.email}</div></div>) },
    { key: 'kind', header: 'Type', render: (r) => <Badge tone={r.kind === 'company' ? 'violet' : 'sky'}>{r.kind === 'company' ? 'Company' : 'Individual'}</Badge> },
    { key: 'sales_manager', header: 'Sales manager', render: (r) => r.sales_manager_name ?? <span className="text-muted">Not assigned</span> },
    { key: 'country', header: 'Country' },
    { key: 'users', header: 'Users', align: 'end', render: (r) => r.active_users },
    { key: 'leads', header: 'Leads', align: 'end' },
    { key: 'status', header: 'Status', render: (r) => <UserStatusBadge status={r.status} /> },
    { key: 'created_at', header: 'Registered', render: (r) => date(r.created_at) },
    { key: 'actions', header: '', align: 'end', render: (r) => (
      <span className="text-nowrap" onClick={(e) => e.stopPropagation()}>
        {can('admin.partners', 'edit') && <button type="button" className="btn btn-edit btn-sm-x me-1" onClick={() => setEditing(r)}><i className="bi bi-pencil me-1" aria-hidden="true" />Edit</button>}
        {can('admin.partners', 'edit') && r.status !== 'active' && <button type="button" className="btn btn-add btn-sm-x me-1" onClick={() => setSt.mutate({ id: r.id, s: 'active' })}>Activate</button>}
        {can('admin.partners', 'edit') && r.status === 'active' && <button type="button" className="btn btn-edit btn-sm-x me-1" onClick={() => setSt.mutate({ id: r.id, s: 'inactive' })}>Deactivate</button>}
        {can('admin.partners', 'edit') && r.status !== 'blocked' && <button type="button" className="btn btn-delete btn-sm-x" onClick={() => setSt.mutate({ id: r.id, s: 'blocked' })}>Block</button>}
      </span>
    ) },
  ];

  return (
    <>
      <PageHeader title="Partner management" subtitle="Register partners, send invitations and control access">
        {can('admin.partners', 'create') && <button type="button" className="btn btn-add btn-sm-x" onClick={() => setCreating(true)}><i className="bi bi-plus-lg me-1" aria-hidden="true" />Register partner</button>}
      </PageHeader>
      <Card bare>
        <div className="d-flex flex-wrap gap-2 p-3 pb-2">
          <SearchInput value={q} onChange={setQ} placeholder="Search name, email, ID…" />
          <select className="form-select form-select-sm w-auto" value={kind} onChange={(e) => setKind(e.target.value)}><option value="">All types</option><option value="individual">Individual</option><option value="company">Company</option></select>
          <select className="form-select form-select-sm w-auto" value={status} onChange={(e) => setStatus(e.target.value)}><option value="">All statuses</option><option value="active">Active</option><option value="inactive">Inactive</option><option value="blocked">Blocked</option></select>
        </div>
        <DataTable columns={columns} rows={rows} isLoading={list.isLoading} error={list.error} onRetry={list.refetch} onRowClick={(r) => setViewing(r)} emptyTitle="No partners registered yet" />
        {list.hasNextPage && <div className="p-3 text-center"><button type="button" className="btn btn-edit btn-sm-x" onClick={() => list.fetchNextPage()}>Load more</button></div>}
      </Card>
      <div className="t-xs text-muted mt-2">Deactivating or blocking a company suspends every member's access; leads and history stay available to the CRM team. Reactivating restores access.</div>
      <RegisterModal open={creating} onClose={() => setCreating(false)} onDone={(p) => { setCreating(false); refresh(); dispatch(pushToast({ tone: 'success', message: `${p.partner_code} registered`, detail: p.invitation?.sent ? 'Invitation emailed.' : p.invitation?.activation_url ?? 'Invitation created — email could not be delivered.' })); }} />
      <PartnerDrawer partner={viewing} onClose={() => setViewing(null)} onEdit={can('admin.partners', 'edit') ? (p) => setEditing(p) : undefined} />
      <EditModal partner={editing} onClose={() => setEditing(null)} onDone={(p) => { setEditing(null); setViewing((v) => (v && v.id === p.id ? p : v)); refresh(); dispatch(pushToast({ tone: 'success', message: `${p.partner_code} updated` })); }} />
    </>
  );
}

function PartnerDrawer({ partner, onClose, onEdit }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const dispatch = useDispatch();
  const managers = useQuery({ queryKey: ['partner-admin', 'sales-managers'], queryFn: api.salesManagers, enabled: Boolean(partner) });
  const [chosen, setChosen] = useState(null);
  const sendReset = useMutation({
    mutationFn: (puId) => api.sendPartnerPasswordReset(partner.id, puId),
    onSuccess: (r, puId) => {
      const to = (u.data ?? []).find((x) => x.id === puId)?.email;
      const what = r.kind === 'invitation' ? 'Activation link' : 'Password reset link';
      dispatch(pushToast({
        tone: r.sent ? 'success' : 'warning',
        message: r.sent ? `${what} emailed` : `${what} created — email could not be delivered`,
        detail: r.sent ? to : (r.reset_url ?? r.activation_url ?? 'Check the mail provider settings.'),
      }));
    },
    onError: (e) => dispatch(pushToast({ tone: 'danger', message: 'Could not send the link', detail: errorText(e) })),
  });
  const changeMgr = useMutation({
    mutationFn: (id) => api.updatePartner(partner.id, { sales_manager_id: id }),
    onSuccess: (p) => { setChosen(p.sales_manager_id); qc.invalidateQueries({ queryKey: ['partner-admin'] }); dispatch(pushToast({ tone: 'success', message: 'Sales manager updated' })); },
    onError: (e) => dispatch(pushToast({ tone: 'danger', message: 'Could not update', detail: errorText(e) })),
  });
  const u = useQuery({ queryKey: ['partner-admin', 'users', partner?.id], queryFn: () => api.partnerUsers(partner.id), enabled: Boolean(partner) });
  return (
    <Drawer open={Boolean(partner)} onClose={onClose} title={partner?.name} subtitle={partner?.partner_code}>
      {partner && (
        <div className="d-flex flex-column gap-3 t-sm">
          {onEdit && <div><button type="button" className="btn btn-edit btn-sm-x" onClick={() => onEdit(partner)}><i className="bi bi-pencil me-1" aria-hidden="true" />Edit partner details</button></div>}
          <div className="row g-2">
            {[['Type', partner.kind], ['Status', partner.status], ['Email', partner.email], ['Phone', partner.phone], ['Website', partner.website], ['Registration no.', partner.registration_number], ['Contact', partner.contact_person], ['Country', partner.country], ['Address', partner.address]].map(([k, v]) => (
              <div className="col-6" key={k}><div className="field-x-label">{k}</div>{v ?? '—'}</div>
            ))}
          </div>
          <div>
            <div className="field-x-label">Sales manager</div>
            {can('admin.partners', 'edit') ? (
              <select className="form-select form-select-sm" disabled={changeMgr.isPending} value={chosen ?? partner.sales_manager_id ?? ''}
                onChange={(e) => e.target.value && changeMgr.mutate(e.target.value)}>
                {!partner.sales_manager_id && chosen == null && <option value="">Not assigned</option>}
                {(managers.data?.data ?? []).map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
              </select>
            ) : (partner.sales_manager_name ?? 'Not assigned')}
          </div>
          <div className="eyebrow">Users</div>
          {u.isLoading ? <Spinner /> : (u.data ?? []).map((x) => (
            <div key={x.id} className="d-flex justify-content-between border-bottom pb-2">
              <div><div className="fw-600">{x.full_name}</div><div className="t-xs text-muted">{x.email} · {ROLE_NAME[x.role]}</div></div>
              <div className="text-end">
                <UserStatusBadge status={x.status} /><div className="t-2xs text-muted">{x.leads_submitted} leads</div>
                {onEdit && (x.status === 'active' || x.status === 'invited') && partner.status === 'active' && (
                  <button type="button" className="btn btn-edit btn-sm-x mt-1" disabled={sendReset.isPending} onClick={() => sendReset.mutate(x.id)}>
                    <i className="bi bi-envelope me-1" aria-hidden="true" />{x.status === 'invited' ? 'Resend invitation' : 'Send reset link'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Drawer>
  );
}

function RegisterModal({ open, onClose, onDone }) {
  const [type, setType] = useState('individual');
  const [f, setF] = useState(BLANK);
  const [errors, setErrors] = useState({});
  const [seen, setSeen] = useState(false);
  if (open && !seen) { setSeen(true); setF(BLANK); setErrors({}); setType('individual'); }
  if (!open && seen) setSeen(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const managers = useQuery({ queryKey: ['partner-admin', 'sales-managers'], queryFn: api.salesManagers, enabled: open });
  const save = useMutation({
    mutationFn: () => {
      const pick = type === 'individual' ? ['sales_manager_id', 'first_name', 'last_name', 'email', 'mobile', 'business_name', 'address', 'country'] : ['sales_manager_id', 'company_name', 'registration_number', 'company_email', 'contact_person', 'contact_number', 'website', 'address', 'country', 'admin_email'];
      const b = { kind: type, ...Object.fromEntries(pick.filter((k) => f[k].trim()).map((k) => [k, f[k].trim()])) };
      return api.createPartner(b);
    },
    onSuccess: onDone, onError: (e) => setErrors({ ...fieldErrors(e), _: errorText(e) }),
  });
  if (!open) return null;
  const need = type === 'individual' ? ['sales_manager_id', 'first_name', 'last_name', 'email', 'mobile', 'country'] : ['sales_manager_id', 'company_name', 'company_email', 'contact_person', 'contact_number', 'country', 'admin_email'];
  const submit = (e) => {
    e.preventDefault();
    const er = {};
    need.forEach((k) => { if (!f[k].trim()) er[k] = 'Required'; });
    ['email', 'company_email', 'admin_email'].forEach((k) => { if (need.includes(k) && f[k] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f[k])) er[k] = 'Enter a valid email'; });
    setErrors(er);
    if (!Object.keys(er).length) save.mutate();
  };
  const fld = (k, label, col = 'col-md-6', props = {}) => (<div className={col}><Field label={label} required={need.includes(k)} error={errors[k]}><input className="form-control" value={f[k]} onChange={set(k)} {...props} /></Field></div>);
  return (
    <Modal open onClose={onClose} title="Register partner" size="lg"
      footer={(<><button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button><button type="submit" form="partnerForm" className="btn btn-save btn-sm-x" disabled={save.isPending}>{save.isPending ? <><ButtonSpinner />Registering…</> : 'Register & send invitation'}</button></>)}>
      <form id="partnerForm" onSubmit={submit} noValidate>
        {errors._ && <div className="alert alert-danger py-2 t-sm">{errors._}</div>}
        <div className="btn-group mb-3" role="group">
          {['individual', 'company'].map((t) => <button key={t} type="button" className={`btn btn-sm ${type === t ? 'btn-save' : 'btn-edit'}`} onClick={() => { setType(t); setErrors({}); }}>{t === 'individual' ? 'Individual partner' : 'Company partner'}</button>)}
        </div>
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <Field label="Sales manager" required error={errors.sales_manager_id} help="Reviews this partner's leads and assigns the sales representative.">
              <select className="form-select" value={f.sales_manager_id} onChange={set('sales_manager_id')}>
                <option value="">{managers.isLoading ? 'Loading…' : 'Select a sales manager…'}</option>
                {(managers.data?.data ?? []).map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
              </select>
            </Field>
          </div>
        </div>
        {type === 'individual' ? (
          <div className="row g-3">
            {fld('first_name', 'First name')}{fld('last_name', 'Last name')}{fld('email', 'Email (login)', 'col-md-6', { type: 'email' })}{fld('mobile', 'Mobile number', 'col-md-6', { type: 'tel' })}
            {fld('business_name', 'Company / business name')}{fld('country', 'Country')}{fld('address', 'Address', 'col-12')}
          </div>
        ) : (
          <div className="row g-3">
            {fld('company_name', 'Company name')}{fld('registration_number', 'Registration number')}{fld('company_email', 'Company email', 'col-md-6', { type: 'email' })}{fld('website', 'Website')}
            {fld('contact_person', 'Contact person')}{fld('contact_number', 'Contact number', 'col-md-6', { type: 'tel' })}{fld('country', 'Country')}{fld('admin_email', 'Partner admin email (login)', 'col-md-6', { type: 'email' })}{fld('address', 'Address', 'col-12')}
          </div>
        )}
        <div className="t-xs text-muted mt-3">The partner's login is created and an activation link is emailed so they can set their password.</div>
      </form>
    </Modal>
  );
}

/** Edit a registered partner — individual or company. The login email is not changed here. */
function EditModal({ partner, onClose, onDone }) {
  const company = partner?.kind === 'company';
  const init = (p) => ({
    name: p.name ?? '', email: p.email ?? '', phone: p.phone ?? '', website: p.website ?? '', registration_number: p.registration_number ?? '',
    contact_person: p.contact_person ?? '', address: p.address ?? '', country: p.country ?? '', sales_manager_id: p.sales_manager_id ?? '',
  });
  const [f, setF] = useState({});
  const [errors, setErrors] = useState({});
  const [seen, setSeen] = useState(null);
  if (partner && seen !== partner.id) { setSeen(partner.id); setF(init(partner)); setErrors({}); }
  if (!partner && seen) setSeen(null);
  const managers = useQuery({ queryKey: ['partner-admin', 'sales-managers'], queryFn: api.salesManagers, enabled: Boolean(partner) });
  const save = useMutation({
    mutationFn: () => {
      const body = {};
      for (const k of Object.keys(f)) {
        const v = typeof f[k] === 'string' ? f[k].trim() : f[k];
        if (v !== (init(partner)[k] ?? '')) body[k] = v;
      }
      return Object.keys(body).length ? api.updatePartner(partner.id, body) : Promise.resolve(partner);
    },
    onSuccess: onDone, onError: (e) => setErrors({ ...fieldErrors(e), _: errorText(e) }),
  });
  if (!partner) return null;
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const need = company ? ['name', 'email', 'contact_person', 'phone', 'country'] : ['name', 'email', 'phone', 'country'];
  const submit = (e) => {
    e.preventDefault();
    const er = {};
    need.forEach((k) => { if (!String(f[k] ?? '').trim()) er[k] = 'Required'; });
    if (f.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) er.email = 'Enter a valid email';
    setErrors(er);
    if (!Object.keys(er).length) save.mutate();
  };
  const fld = (k, label, col = 'col-md-6', props = {}) => (
    <div className={col}><Field label={label} required={need.includes(k)} error={errors[k]}><input className="form-control" value={f[k] ?? ''} onChange={set(k)} {...props} /></Field></div>
  );
  return (
    <Modal open onClose={onClose} title={`Edit ${company ? 'company' : 'individual'} partner · ${partner.partner_code}`} size="lg"
      footer={(<><button type="button" className="btn btn-cancel btn-sm-x" onClick={onClose}>Cancel</button><button type="submit" form="partnerEditForm" className="btn btn-save btn-sm-x" disabled={save.isPending}>{save.isPending ? <><ButtonSpinner />Saving…</> : 'Save changes'}</button></>)}>
      <form id="partnerEditForm" onSubmit={submit} noValidate>
        {errors._ && <div className="alert alert-danger py-2 t-sm">{errors._}</div>}
        <div className="row g-3">
          <div className="col-md-6">
            <Field label="Sales manager" help="Reviews this partner's leads.">
              <select className="form-select" value={f.sales_manager_id ?? ''} onChange={set('sales_manager_id')}>
                {!partner.sales_manager_id && <option value="">Not assigned</option>}
                {(managers.data?.data ?? []).map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
              </select>
            </Field>
          </div>
          <div className="col-md-6" />
          {fld('name', company ? 'Company name' : 'Full name')}
          {company ? fld('registration_number', 'Registration number') : fld('contact_person', 'Company / business name')}
          {fld('email', company ? 'Company email' : 'Email', 'col-md-6', { type: 'email' })}
          {fld('phone', company ? 'Contact number' : 'Mobile number', 'col-md-6', { type: 'tel' })}
          {company && fld('contact_person', 'Contact person')}
          {company && fld('website', 'Website')}
          {fld('country', 'Country')}
          {fld('address', 'Address', 'col-12')}
        </div>
        <div className="t-xs text-muted mt-3">The login email and password are managed from the partner's invitation; changing the email above does not change the login.</div>
      </form>
    </Modal>
  );
}
